import { getInitialShippingRefundInCents } from "../lib/initial-shipping-refund.lib";
import { applyFeeAndBonus, type NetTotals } from "../lib/return-financials.lib";
import {
  resolvePaidReturnMethod,
  type PaidReturnMethod,
} from "../lib/return-fee.lib";
import { usePublicShop } from "../queries/use-public-shop";
import type { FinancialSummary, Order } from "../types";
import { useDiscountBonuses } from "./use-discount-bonuses";
import { useDraftReturn } from "./use-draft-return";
import { useFinancialSummary } from "./use-financial-summary";
import { useHypotheticalReturnFee } from "./use-hypothetical-return-fee";

export type ReturnFinancials = NetTotals & {
  summary: FinancialSummary;
  /** Return fee for the basket as it stands, in cents, after any merchant waiver. */
  fee: number;
  /** Return fee before a merchant waiver, used to decide whether the waiver option applies. */
  grossFee: number;
  /** Shopify tax included in `fee` (BBS-366, US orders), in cents. */
  feeTax: number;
  isFeePending: boolean;
  feeMethod: PaidReturnMethod | null;
  bonusTotal: number;
  bonusCurrency: string | null;
  initialShippingRefund: number;
};

/**
 * Everything the receipt needs to announce what the customer will actually get:
 * gross totals, return fee, store-credit bonus and initial shipping refund.
 */
export function useReturnFinancials(
  orderId: string,
  shopId: string,
  order: Order | null,
): ReturnFinancials {
  const summary = useFinancialSummary(orderId, order);
  const draft = useDraftReturn(orderId);
  const { data: shop } = usePublicShop(shopId);
  const bonuses = useDiscountBonuses(orderId, shopId);
  const feeState = useHypotheticalReturnFee("current", { orderId, shopId });

  const lineItems = draft?.lineItems ?? [];
  const feeMethod = resolvePaidReturnMethod(lineItems);
  const isFeePending =
    feeState.status === "loading" ||
    (feeState.status === "ready" && feeState.isStale);
  const grossFee = feeState.status === "loading" ? 0 : feeState.fee;
  // Merchant-only override: staff can waive the return fee when creating the request.
  const returnShippingPaid = draft?.returnShippingPaid ?? true;
  const fee = returnShippingPaid ? grossFee : 0;
  const feeTax =
    returnShippingPaid && feeState.status === "ready" ? feeState.tax : 0;

  const initialShippingRefund =
    order && shop ? getInitialShippingRefundInCents(lineItems, order, shop) : 0;
  // The legacy receipt gives the initial shipping back through the store credit as soon
  // as one article is resolved that way, and through the refund otherwise.
  const shippingMethod = summary.discountTotal > 0 ? "discount" : "refund";

  const nets = applyFeeAndBonus(summary, {
    fee,
    feeMethod,
    bonusTotal: bonuses.total,
    bonusCurrency: bonuses.currency,
    initialShippingRefund,
    shippingMethod,
  });

  return {
    ...nets,
    summary,
    fee,
    grossFee,
    feeTax,
    isFeePending,
    feeMethod,
    bonusTotal: bonuses.total,
    bonusCurrency: bonuses.currency,
    initialShippingRefund,
  };
}
